import type { Theme } from '@hivebrite/design-system/Theme';
import { convertRemToPx } from '@hivebrite/design-system/Theme';

import type {
  UserBannerImageChildren,
  UserBannerChildren,
} from 'hb-builder/components/BuilderComponents/page/block/UserBanner/types/UserBannerChildren';
import type { FeatureConfig } from 'hb-builder/types';
import { createNewItem } from 'hb-builder/utils/createNewItem';
import type { DocumentItem } from 'hb-react/backoffice/types/builder/builderDocument';
import type { ButtonItemAttributes } from 'hb-react/backoffice/types/builder/itemAttributes/buttonItemAttributes';
import type { TextItemAttributes } from 'hb-react/backoffice/types/builder/itemAttributes/textItemAttributes';
import type { FeatureFlags } from 'hb-react/types/featureFlags';

export const getUserBannerItemChildren = (props: {
  components: Record<string, any>;
  featureFlags: FeatureFlags;
  features: FeatureConfig;
  theme?: Theme;
}): UserBannerChildren => {
  const { components, features, theme } = props;
  const { Button, Text } = components;

  const image: Omit<UserBannerImageChildren, 'id'> = {
    attributes: {
      url: '/images/missing/user_lane/avatar.png',
      alt: '',
    },
    type: '',
  };

  const headline: Omit<DocumentItem<TextItemAttributes>, 'id'> = Text.item({
    ...props,
    features: {
      ...features,
      text: {
        ...features?.text,
        containerPadding: 0,
        content: __('Welcome back %{First_Name} %{Last_Name} 👋'),
        defaultTextColor: true,
        horizontalAlign: 'left',
        textColor: '#000',
        textFontSize: 42,
        tied: true,
      },
    },
  });

  const buttonPost: Omit<
    DocumentItem<ButtonItemAttributes>,
    'id'
  > = Button.item({
    ...props,
    features: {
      ...features,
      button: {
        ...features?.button,
        borderRadius: theme?.buttons.secondary.borderRadius,
        content: __('⏱ My recent posts'),
        containerPadding: '0px 12px 12px 0px',
        hidden: false,
        horizontalAlign: 'left',
        lineHeight: theme
          ? convertRemToPx(theme.lineHeights[1] as unknown as number)
          : undefined,
        link: `${window.location.origin}/users/%{User_ID}/activity`,
        tied: true,
        variant: 'secondary',
      },
    },
  });

  const buttonEvent: Omit<
    DocumentItem<ButtonItemAttributes>,
    'id'
  > = Button.item({
    ...props,
    features: {
      ...features,
      button: {
        ...features?.button,
        borderRadius: theme?.buttons.secondary.borderRadius,
        content: __('📆 My events'),
        containerPadding: '0px 12px 12px 0px',
        hidden: false,
        horizontalAlign: 'left',
        lineHeight: theme
          ? convertRemToPx(theme.lineHeights[1] as unknown as number)
          : undefined,
        link: `${window.location.origin}/users/%{User_ID}/my-events`,
        tied: true,
        variant: 'secondary',
      },
    },
  });

  const buttonPortfolio: Omit<
    DocumentItem<ButtonItemAttributes>,
    'id'
  > = Button.item({
    ...props,
    features: {
      ...features,
      button: {
        ...features?.button,
        borderRadius: theme?.buttons.secondary.borderRadius,
        containerPadding: '0px 12px 12px 0px',
        content: __('🎨 My portfolio'),
        hidden: false,
        horizontalAlign: 'left',
        lineHeight: theme
          ? convertRemToPx(theme.lineHeights[1] as unknown as number)
          : undefined,
        link: `${window.location.origin}/users/%{User_ID}/portfolio`,
        tied: true,
        variant: 'secondary',
      },
    },
  });

  const buttonAccount: Omit<
    DocumentItem<ButtonItemAttributes>,
    'id'
  > = Button.item({
    ...props,
    features: {
      ...features,
      button: {
        ...features?.button,
        borderRadius: theme?.buttons.secondary.borderRadius,
        content: __('⚙️ My account'),
        containerPadding: '0px 0px 12px 0px',
        hidden: false,
        horizontalAlign: 'left',
        lineHeight: theme
          ? convertRemToPx(theme.lineHeights[1] as unknown as number)
          : undefined,
        link: `${window.location.origin}/users/%{User_ID}`,
        tied: true,
        variant: 'secondary',
      },
    },
  });

  const profileCompletion: Omit<
    DocumentItem<TextItemAttributes>,
    'id'
  > = Text.item({
    ...props,
    features: {
      ...features,
      text: {
        ...features?.text,
        containerPadding: 0,
        content: __('Profile completed %{percentage}%').i__({
          percentage: '0',
        }),
        defaultTextColor: true,
        hidden: false,
        horizontalAlign: 'left',
        textColor: '#555555',
        textFontSize: theme
          ? convertRemToPx(theme.fontSizes[2] as unknown as number)
          : undefined,
        tied: true,
      },
    },
  });

  return [
    createNewItem(image),
    createNewItem(headline),
    createNewItem(buttonPost),
    createNewItem(buttonEvent),
    createNewItem(buttonPortfolio),
    createNewItem(buttonAccount),
    createNewItem(profileCompletion),
  ];
};
